import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SidebarLayout from "./SidebarLayout";
import BusinessSelector from "../../components/BusinessSelector";
import { getMyBusinesses, getMyShopsV2 } from "../../services/businessService";
import { getStorefrontSetupStatus } from "../../services/storefrontService";
import { IMAGE_UPLOAD_HINT } from "../../utils/imageUploadPolicy";
import StorefrontSetupStatusBanner from "../../components/storefront/StorefrontSetupStatusBanner";
import VariantEditor from "../../components/VariantEditor";
import ImageUploadField from "../../components/ImageUploadField";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const normalizeCategoryOption = (category) => {
  if (!category) return null;

  if (typeof category === "string") {
    const value = category.trim();
    if (!value) return null;
    return {
      value,
      label: value,
      icon: "",
    };
  }

  const rawValue =
    category.value || category.slug || category.key || category.name || category.label;
  const value = String(rawValue || "").trim();
  if (!value) return null;

  return {
    ...category,
    value,
    label: String(category.label || category.name || value).trim(),
    icon: category.icon || "",
  };
};

// Phase C: Preset dimensions library for multi-variant path
const PRESET_DIMENSIONS = {
  sizes: {
    name: "Size",
    key: "size",
    values: [
      { label: "Extra Small (XS)", value: "xs" },
      { label: "Small (S)", value: "s" },
      { label: "Medium (M)", value: "m" },
      { label: "Large (L)", value: "l" },
      { label: "Extra Large (XL)", value: "xl" },
      { label: "2XL", value: "2xl" },
      { label: "3XL", value: "3xl" },
    ],
  },
  volumes: {
    name: "Volume",
    key: "volume",
    values: [
      { label: "10ml", value: "10ml" },
      { label: "25ml", value: "25ml" },
      { label: "50ml", value: "50ml" },
      { label: "100ml", value: "100ml" },
    ],
  },
  weights: {
    name: "Weight",
    key: "weight",
    values: [
      { label: "1 lb", value: "1lb" },
      { label: "2 lb", value: "2lb" },
      { label: "5 lb", value: "5lb" },
      { label: "10 lb", value: "10lb" },
    ],
  },
  scents: {
    name: "Scent",
    key: "scent",
    values: [
      { label: "Lavender", value: "lavender" },
      { label: "Rose", value: "rose" },
      { label: "Jasmine", value: "jasmine" },
      { label: "Vanilla", value: "vanilla" },
      { label: "Mint", value: "mint" },
      { label: "Citrus", value: "citrus" },
      { label: "Coconut", value: "coconut" },
      { label: "Unscented", value: "unscented" },
    ],
  },
  colors: {
    name: "Color",
    key: "color",
    values: [
      { label: "Black", value: "black" },
      { label: "White", value: "white" },
      { label: "Red", value: "red" },
      { label: "Blue", value: "blue" },
      { label: "Green", value: "green" },
      { label: "Pink", value: "pink" },
      { label: "Brown", value: "brown" },
      { label: "Blonde", value: "blonde" },
    ],
  },
};

const ProductsV2 = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [creationMode, setCreationMode] = useState("single"); // Phase C: "single" or "multi"

  // Master product form data
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    basePrice: "",
    baseStock: "",
    category: [],
    brand: "",
    sku: "",
    lowStockThreshold: 5,
    images: [],
    isFeatured: false,
    recommendedForServices: [],
  });

  // Location inventory (dynamic array)
  const [locationInventory, setLocationInventory] = useState([
    { shopId: "", quantity: 0, price: "", useMasterPrice: true },
  ]);
  const [useSamePriceForAll, setUseSamePriceForAll] = useState(true);
  const [hasVariants, setHasVariants] = useState(true);
  const [variantDimensions, setVariantDimensions] = useState([]);
  const [variants, setVariants] = useState([]);

  const [availableShops, setAvailableShops] = useState([]);
  const [services, setServices] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [storefrontStatus, setStorefrontStatus] = useState(null);

  // Bulk operations
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  // Image upload
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [maxProductImagesPerProduct, setMaxProductImagesPerProduct] = useState(8);

  // Inventory Management
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [selectedProductForInventory, setSelectedProductForInventory] =
    useState(null);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [restockData, setRestockData] = useState({
    shopProductId: "",
    shopId: "",
    shopName: "",
    variantLabel: "",
    quantity: 0,
    notes: "",
  });

  const isStorefrontSetupIncomplete =
    storefrontStatus &&
    Array.isArray(storefrontStatus.missingRequirements) &&
    storefrontStatus.missingRequirements.length > 0;
  const onboardingStage = storefrontStatus?.onboardingStage || "hard";
  const setupUiEnabled = onboardingStage !== "off";
  const softGateEnabled = onboardingStage === "soft";
  const hardGateEnabled = onboardingStage === "hard";
  const setupPrimaryCategories = Array.isArray(
    storefrontStatus?.fields?.primaryCategories,
  )
    ? storefrontStatus.fields.primaryCategories
    : [];
  const setupPrimaryCategorySet = new Set(
    setupPrimaryCategories.map((v) => String(v).trim().toLowerCase()),
  );
  const setupScopedCategories =
    setupPrimaryCategorySet.size > 0
      ? categories.filter((cat) =>
          setupPrimaryCategorySet.has(String(cat.value).trim().toLowerCase()),
        )
      : categories;
  const categoryOptionsBase =
    setupPrimaryCategorySet.size > 0 ? setupScopedCategories : categories;
  const missingSelectedCategoryOptions = (formData.category || [])
    .filter(
      (selectedValue) =>
        !categoryOptionsBase.some(
          (cat) => String(cat.value).trim().toLowerCase() === String(selectedValue).trim().toLowerCase(),
        ),
    )
    .map((value) => normalizeCategoryOption(value))
    .filter(Boolean);
  const categoryOptionsForForm = [
    ...categoryOptionsBase,
    ...missingSelectedCategoryOptions,
  ];
  const trimmedDescriptionLength = (formData.description || "").trim().length;

  const confirmSoftPublishWarning = (actionLabel) => {
    if (!softGateEnabled) return true;
    if (!isStorefrontSetupIncomplete) return true;
    const missing = (
      storefrontStatus.missingRequirementLabels ||
      storefrontStatus.missingRequirements ||
      []
    ).join(", ");
    return window.confirm(
      `Storefront setup is incomplete (${missing}). You can continue for now, but publishing may be restricted later. Continue to ${actionLabel}?`,
    );
  };

  const handleStoreSetupBlockedError = (error) => {
    const code = error?.response?.data?.error;
    if (code !== "STORE_SETUP_INCOMPLETE") return false;

    const message =
      error?.response?.data?.message ||
      "Complete storefront setup before activating products.";
    const blocking =
      error?.response?.data?.blockingRequirementLabels ||
      error?.response?.data?.missingRequirementLabels ||
      [];
    const reasonLine =
      Array.isArray(blocking) && blocking.length > 0
        ? `\n\nBlocked by: ${blocking.join(", ")}`
        : "";
    const setupUrl =
      error?.response?.data?.setupUrl || "/dashboard/products/storefront-setup";

    if (window.confirm(`${message}${reasonLine}\n\nOpen storefront setup now?`)) {
      navigate(setupUrl);
    }
    return true;
  };

  useEffect(() => {
    fetchUserShops();
    fetchCategories();
    fetchPublicSettings();
  }, []);

  const fetchPublicSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/settings/public`);
      const configuredLimit = Number(response?.data?.settings?.maxProductImagesPerProduct);
      if (Number.isFinite(configuredLimit) && configuredLimit > 0) {
        setMaxProductImagesPerProduct(configuredLimit);
      }
    } catch (error) {}
  };

  useEffect(() => {
    let mounted = true;

    const fetchStorefrontStatus = async () => {
      try {
        const data = await getStorefrontSetupStatus();
        if (mounted && data?.success) {
          setStorefrontStatus(data);
        }
      } catch (error) {}
    };

    fetchStorefrontStatus();

    return () => {
      mounted = false;
    };
  }, []);

  const extractBusinessId = (shop) => {
    if (!shop) return null;
    return shop.businessId?._id || shop.businessId || null;
  };

  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchUserShops = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const businessesData = await getMyBusinesses();
      setBusinesses(businessesData);

      const shopsData = await getMyShopsV2();
      setAvailableShops(shopsData);

      if (shopsData.length > 0) {
        const firstBusinessId =
          businessesData[0]?._id || extractBusinessId(shopsData[0]);

        if (firstBusinessId) {
          setSelectedBusinessId(firstBusinessId);
          fetchProducts(firstBusinessId);
        }

        fetchServices(shopsData[0]._id);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessChange = async (businessId) => {
    setSelectedBusinessId(businessId);
    try {
      const shopsData = await getMyShopsV2();
      const filteredShops = shopsData.filter(
        (shop) => String(extractBusinessId(shop)) === String(businessId),
      );
      setAvailableShops(filteredShops);

      if (filteredShops.length > 0) {
        fetchProducts(businessId);
        fetchServices(filteredShops[0]._id);
      }
    } catch (error) {}
  };

  const fetchProducts = async (businessId) => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v2/products/business/${businessId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProducts(response.data.products || []);
    } catch (error) {
      setProducts([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/platform-settings/product-categories`,
      );
      // API returns array directly, not wrapped in {categories: [...]}
      const cats = Array.isArray(response.data) ? response.data : [];
      setCategories(
        cats
          .map(normalizeCategoryOption)
          .filter((category) => Boolean(category?.value)),
      );
    } catch (error) {
      setCategories([]);
    }
  };

  const fetchServices = async (shopId) => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/staff/shop/${shopId}/services`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setServices(response.data.services || []);
    } catch (error) {}
  };

  const addLocationRow = () => {
    const selectedShopIds = new Set(
      locationInventory
        .map((loc) => String(loc.shopId || "").trim())
        .filter(Boolean),
    );

    if (selectedShopIds.size >= availableShops.length) {
      alert("You have already added all available locations");
      return;
    }

    const nextShop = availableShops.find(
      (shop) => !selectedShopIds.has(String(shop._id || "").trim()),
    );

    setLocationInventory([
      ...locationInventory,
      {
        shopId: nextShop ? nextShop._id : "",
        quantity: 0,
        price: "",
        useMasterPrice: useSamePriceForAll,
      },
    ]);
  };

  const removeLocationRow = (index) => {
    if (locationInventory.length === 1) {
      alert("You must have at least one location");
      return;
    }
    const updated = locationInventory.filter((_, i) => i !== index);
    setLocationInventory(updated);
  };

  const updateLocationRow = (index, field, value) => {
    const updated = [...locationInventory];

    if (field === "shopId") {
      const incomingShopId = String(value || "").trim();
      if (incomingShopId) {
        const duplicateIndex = updated.findIndex(
          (loc, i) =>
            i !== index && String(loc.shopId || "").trim() === incomingShopId,
        );
        if (duplicateIndex !== -1) {
          alert("This shop location is already selected. Choose a different location.");
          return;
        }
      }
    }

    updated[index][field] = value;

    // If updating price field, automatically set useMasterPrice to false for this location
    if (field === "price" && value !== "") {
      updated[index].useMasterPrice = false;
      // Uncheck the global toggle since we now have custom pricing
      setUseSamePriceForAll(false);
    }

    // If updating useMasterPrice for a specific location, check if all should be updated
    if (field === "useMasterPrice") {
      // Check if all locations now have the same useMasterPrice value
      const allSamePrice = updated.every((loc) => loc.useMasterPrice === value);
      setUseSamePriceForAll(allSamePrice);
    }

    setLocationInventory(updated);
  };

  const handleUseSamePriceChange = (checked) => {
    setUseSamePriceForAll(checked);

    // Update all locations
    const updated = locationInventory.map((loc) => ({
      ...loc,
      useMasterPrice: checked,
      price: checked ? "" : loc.price,
    }));
    setLocationInventory(updated);
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    setUploadingImage(true);
    const token = localStorage.getItem("ogestyle_token");

    try {
      const formData = new FormData();
      formData.append("image", imageFile);

      const response = await axios.post(
        `${API_BASE_URL}/api/freelancer/products/upload-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data.imageUrl;
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to upload image");
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImagesUploaded = (uploaded) => {
    const uploadedUrls = Array.isArray(uploaded) ? uploaded.filter(Boolean) : [];
    if (uploadedUrls.length === 0) return;

    setFormData((prev) => ({
      ...prev,
      images: [...(prev.images || []), ...uploadedUrls].slice(
        0,
        maxProductImagesPerProduct,
      ),
    }));
    setImagePreview(uploadedUrls[0] || null);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    // Only URLs are saved on the product — never base64.
    setFormData((prev) => ({ ...prev, images: [] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (
      !confirmSoftPublishWarning(
        editingProduct ? "update this product" : "create this product",
      )
    ) {
      return;
    }

    // Validate category selection
    if (!formData.category || formData.category.length === 0) {
      alert("Please select at least one category");
      return;
    }

    if (!selectedBusinessId) {
      alert("Please select a business before saving this product.");
      return;
    }

    const normalizedDescription = String(formData.description || "").trim();
    if (!normalizedDescription || normalizedDescription.length < 30) {
      alert("Description is required and must be at least 30 characters.");
      return;
    }

    const token = localStorage.getItem("ogestyle_token");
    setIsSubmitting(true);

    try {
      // Validate at least one location
      const uniqueLocationMap = new Map();
      locationInventory.forEach((loc) => {
        const key = String(loc?.shopId || "").trim();
        if (!key || uniqueLocationMap.has(key)) return;
        uniqueLocationMap.set(key, {
          ...loc,
          shopId: key,
        });
      });

      const validLocations = Array.from(uniqueLocationMap.values()).filter(
        (loc) => loc.shopId && loc.quantity >= 0,
      );
      if (validLocations.length === 0) {
        alert("Please add at least one location with inventory");
        setIsSubmitting(false);
        return;
      }

      // Category is already an array from the checkbox selection
      const categoryArray = Array.isArray(formData.category)
        ? formData.category.filter((c) => c && c.trim())
        : [formData.category].filter((c) => c && c.trim());

      const normalizedVariants = Array.isArray(variants)
        ? variants
            .map((variant, index) => ({
              ...variant,
              variantKey:
                String(variant?.variantKey || "").trim() ||
                `variant-${index + 1}`,
              variantLabel:
                String(variant?.variantLabel || "").trim() ||
                `Variant ${index + 1}`,
              variantSku:
                String(variant?.variantSku || "").trim() ||
                (String(formData.sku || "").trim()
                  ? `${String(formData.sku).trim()}-${String(
                      variant?.variantKey || `variant-${index + 1}`,
                    )
                      .replace(/[^a-zA-Z0-9]+/g, "-")
                      .replace(/^-+|-+$/g, "")
                      .toUpperCase()}`
                  : ""),
              price:
                variant?.price !== undefined && variant?.price !== null
                  ? parseFloat(variant.price)
                  : 0,
              quantity:
                variant?.quantity !== undefined && variant?.quantity !== null
                  ? parseInt(variant.quantity, 10) || 0
                  : 0,
            }))
            .filter((variant) => String(variant.variantKey || "").trim().length > 0)
        : [];

      const parsedFormBasePrice = parseFloat(formData.basePrice);
      const parsedFormBaseStock = parseInt(formData.baseStock, 10);
      const resolvedBaseStock =
        Number.isFinite(parsedFormBaseStock) && parsedFormBaseStock >= 0
          ? parsedFormBaseStock
          : 0;
      if (
        creationMode === "single" &&
        (!Number.isFinite(parsedFormBasePrice) || parsedFormBasePrice < 0)
      ) {
        alert("Please enter a valid product price for single-variant products.");
        setIsSubmitting(false);
        return;
      }
      if (
        creationMode === "single" &&
        (!Number.isFinite(parsedFormBaseStock) || parsedFormBaseStock < 0)
      ) {
        alert("Please enter a valid stock quantity for single-variant products.");
        setIsSubmitting(false);
        return;
      }

      const variantPayload =
        normalizedVariants.length > 0
          ? normalizedVariants
          : [
              {
                id: `variant-default-${Date.now()}`,
                variantKey: "default",
                variantLabel: "Default",
                variantAttributes: [],
                variantSku: "",
                variantImages: [],
                quantity: resolvedBaseStock,
                price: Number.isFinite(parsedFormBasePrice) ? parsedFormBasePrice : 0,
                variantIsActive: true,
              },
            ];

      const activeVariants = variantPayload.filter(
        (variant) => variant.variantIsActive !== false,
      );
      if (activeVariants.length === 0) {
        alert("At least one active variant is required.");
        setIsSubmitting(false);
        return;
      }

      const variantKeySet = new Set();
      for (const variant of variantPayload) {
        const key = String(variant.variantKey || "").trim().toLowerCase();
        if (variantKeySet.has(key)) {
          alert(`Duplicate variant key detected: ${variant.variantKey}`);
          setIsSubmitting(false);
          return;
        }
        variantKeySet.add(key);
      }

      const variantSkuSet = new Set();
      for (const variant of variantPayload) {
        const sku = String(variant.variantSku || "").trim().toLowerCase();
        if (!sku) continue;
        if (variantSkuSet.has(sku)) {
          alert(`Duplicate variant SKU detected: ${variant.variantSku}`);
          setIsSubmitting(false);
          return;
        }
        variantSkuSet.add(sku);
      }

      const invalidPriceVariant = variantPayload.find(
        (variant) => variant.variantIsActive !== false && !Number.isFinite(parseFloat(variant.price)),
      );
      if (invalidPriceVariant) {
        alert("Every active variant must have a valid price.");
        setIsSubmitting(false);
        return;
      }

      const activeVariantPrices = variantPayload
        .filter((variant) => variant.variantIsActive !== false)
        .map((variant) => parseFloat(variant.price))
        .filter((value) => Number.isFinite(value));

      const resolvedBasePrice =
        creationMode === "single"
          ? parsedFormBasePrice
          : activeVariantPrices.length > 0
            ? Math.min(...activeVariantPrices)
            : 0;

      const payload = {
        // Master product fields
        name: formData.name,
        description: formData.description,
        category: categoryArray,
        brand: formData.brand,
        sku: String(formData.sku || "").trim() || undefined,
        basePrice: resolvedBasePrice,
        images: (formData.images || []).slice(0, maxProductImagesPerProduct),
        isFeatured: formData.isFeatured,
        isActive: hardGateEnabled && isStorefrontSetupIncomplete ? false : true,
        lowStockThreshold: parseInt(formData.lowStockThreshold) || 5,
        recommendedForServices: formData.recommendedForServices,
        businessId: selectedBusinessId,
        // Phase C: hasVariants depends on creationMode
        hasVariants: creationMode === "multi",
        variantDimensions: creationMode === "multi" ? variantDimensions : [],
        variants: creationMode === "multi" ? variantPayload : [
          {
            id: `variant-default-${Date.now()}`,
            variantKey: "default",
            variantLabel: "Default",
            variantAttributes: [],
            variantSku: "",
            variantImages: [],
            quantity: resolvedBaseStock,
            price: Number.isFinite(parsedFormBasePrice) ? parsedFormBasePrice : 0,
            variantIsActive: true,
          },
        ],

        // Location inventory
        locations: validLocations.map((loc) => ({
          shopId: loc.shopId,
          quantity: 0,
          price:
            loc.useMasterPrice
              ? null
              : parseFloat(loc.price) || null,
          useMasterPrice: true,
        })),
      };

      if (editingProduct) {
        await axios.put(
          `${API_BASE_URL}/api/v2/products/${editingProduct._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        alert("Product updated successfully!");
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/api/v2/products`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (hardGateEnabled && isStorefrontSetupIncomplete) {
          alert(
            `Product saved as draft with ${response.data?.stats?.variantCount || variantPayload.length} variant(s) across ${response.data?.stats?.locationCount || validLocations.length} location(s). Complete storefront setup to publish.`,
          );
        } else {
          alert(
            `Product created with ${response.data?.stats?.variantCount || variantPayload.length} variant(s) across ${response.data?.stats?.locationCount || validLocations.length} location(s)!`,
          );
        }
      }

      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts(selectedBusinessId);
    } catch (error) {
      if (handleStoreSetupBlockedError(error)) {
        return;
      }
      const backendErrorCode = error?.response?.data?.error;
      const backendMessage = error?.response?.data?.message;
      if (backendErrorCode || backendMessage) {
        console.error("Product save failed", {
          code: backendErrorCode,
          message: backendMessage,
          details: error?.response?.data,
        });
      }
      alert(
        backendErrorCode
          ? `${backendErrorCode}: ${backendMessage || "Failed to save product"}`
          : backendMessage || "Failed to save product",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const productVariants = Array.isArray(product.variants) ? product.variants : [];
    const variantSkuPrefixes = productVariants
      .map((variant) => String(variant?.variantSku || "").trim())
      .filter(Boolean)
      .map((sku) => sku.split("-")[0]?.trim())
      .filter(Boolean);
    const uniqueVariantSkuPrefixes = Array.from(new Set(variantSkuPrefixes));
    const inferredSkuPrefix =
      uniqueVariantSkuPrefixes.length === 1 ? uniqueVariantSkuPrefixes[0] : "";

    const inferredBaseStock =
      Array.isArray(product.variants) && product.variants.length > 0
        ? parseInt(product.variants[0]?.stock ?? product.variants[0]?.quantity ?? 0, 10) || 0
        : parseInt(product.totalStock, 10) || 0;

    setFormData({
      name: product.name,
      description: product.description || "",
      basePrice: String(product.basePrice ?? ""),
      baseStock: String(inferredBaseStock),
      category: Array.isArray(product.category)
        ? product.category
        : product.category
          ? [product.category]
          : [],
      brand: product.brand || "",
      sku: String(product.sku || "").trim() || inferredSkuPrefix,
      lowStockThreshold: product.lowStockThreshold,
      images: product.images || [],
      isFeatured: product.isFeatured || false,
      recommendedForServices:
        product.recommendedForServices?.map((s) =>
          typeof s === "object" ? s._id : s,
        ) || [],
    });

    // Phase C: Detect creation mode based on existing product structure
    const hasMultiVariants =
      product.hasVariants === true ||
      (Array.isArray(product.variantDimensions) && product.variantDimensions.length > 0);
    setCreationMode(hasMultiVariants ? "multi" : "single");

    setHasVariants(hasMultiVariants);
    setVariantDimensions(Array.isArray(product.variantDimensions) ? product.variantDimensions : []);
    setVariants(
      Array.isArray(product.variants) && product.variants.length > 0
        ? product.variants.map((variant, index) => ({
            id: variant.id || variant._id || `variant-${index}-${Date.now()}`,
            variantKey: variant.variantKey || "default",
            variantLabel: variant.variantLabel || "Default",
            variantAttributes: Array.isArray(variant.variantAttributes)
              ? variant.variantAttributes
              : [],
            variantSku: variant.variantSku || "",
            variantImages: Array.isArray(variant.variantImages)
              ? variant.variantImages
              : [],
            quantity:
              parseInt(
                variant.stock ?? variant.quantity ?? variant.quantityInStock ?? 0,
                10,
              ) || 0,
            price: Number.isFinite(parseFloat(variant.price))
              ? parseFloat(variant.price)
              : 0,
            variantIsActive: variant.variantIsActive !== false,
          }))
        : [
            {
              id: `variant-default-${Date.now()}`,
              variantKey: "default",
              variantLabel: "Default",
              variantAttributes: [],
              variantSku: "",
              variantImages: [],
              quantity: product.totalStock || 0,
              price: 0,
              variantIsActive: true,
            },
          ],
    );

    // Set image preview for existing images
    if (product.images && product.images.length > 0) {
      setImagePreview(product.images[0]);
    } else {
      setImagePreview(null);
    }
    setImageFile(null);

    // Set location inventory from product.locations
    if (product.locations && product.locations.length > 0) {
      const dedupedLocationMap = new Map();
      product.locations.forEach((loc) => {
        const shopId = String(
          typeof loc.shopId === "object" ? loc.shopId._id : loc.shopId,
        ).trim();
        if (!shopId || dedupedLocationMap.has(shopId)) return;
        dedupedLocationMap.set(shopId, {
          shopId,
          quantity: loc.stock || 0,
          price: loc.price ? loc.price.toString() : "",
          useMasterPrice: loc.useMasterPrice !== false,
        });
      });
      const locationData = Array.from(dedupedLocationMap.values());
      setLocationInventory(locationData);

      // Check if all locations use master price
      const allUsingMasterPrice = locationData.every(
        (loc) => loc.useMasterPrice,
      );
      setUseSamePriceForAll(allUsingMasterPrice);
    }

    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this product? This will remove it from all locations.",
      )
    )
      return;

    const token = localStorage.getItem("ogestyle_token");
    try {
      await axios.delete(`${API_BASE_URL}/api/v2/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("Product deleted successfully");
      fetchProducts(selectedBusinessId);
    } catch (error) {
      alert("Failed to delete product");
    }
  };

  // Inventory Management Functions
  const handleViewInventory = (product) => {
    setSelectedProductForInventory(product);
    setShowInventoryModal(true);
  };

  const handleRestockClick = (location, productName) => {
    setRestockData({
      shopProductId: location._id,
      shopId: location.shopId._id || location.shopId,
      shopName: location.shopId.name || "Unknown Location",
      variantLabel: location.variantLabel || "Default",
      quantity: 0,
      notes: "",
    });
    setShowRestockModal(true);
  };

  const handleRestockSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("ogestyle_token");
    const quantity = parseInt(restockData.quantity);

    if (quantity <= 0) {
      alert("Please enter a valid quantity");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/v2/products/inventory/restock`,
        {
          shopProductId: restockData.shopProductId,
          quantity,
          notes: restockData.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert(
        `Successfully added ${quantity} units to ${restockData.shopName}${restockData.variantLabel ? ` (${restockData.variantLabel})` : ""}`,
      );
      setShowRestockModal(false);
      setRestockData({
        shopProductId: "",
        shopId: "",
        shopName: "",
        variantLabel: "",
        quantity: 0,
        notes: "",
      });

      // Refresh product data
      fetchProducts(selectedBusinessId);

      // Refresh inventory modal if open
      if (selectedProductForInventory) {
        const response = await axios.get(
          `${API_BASE_URL}/api/v2/products/business/${selectedBusinessId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const updatedProduct = response.data.products.find(
          (p) => p._id === selectedProductForInventory._id,
        );
        if (updatedProduct) {
          setSelectedProductForInventory(updatedProduct);
        }
      }
    } catch (error) {
      alert("Failed to restock. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bulk Operations Handlers
  const handleBulkActivate = async () => {
    if (selectedProducts.length === 0) return;
    if (
      !window.confirm(`Activate ${selectedProducts.length} selected products?`)
    )
      return;
    if (
      !confirmSoftPublishWarning(
        `activate ${selectedProducts.length} product(s)`,
      )
    )
      return;

    const token = localStorage.getItem("ogestyle_token");
    try {
      await Promise.all(
        selectedProducts.map((id) =>
          axios.put(
            `${API_BASE_URL}/api/v2/products/${id}`,
            { isActive: true },
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ),
      );
      alert(`${selectedProducts.length} products activated!`);
      setSelectedProducts([]);
      setSelectionMode(false);
      fetchProducts(selectedBusinessId);
    } catch (error) {
      if (handleStoreSetupBlockedError(error)) {
        return;
      }
      alert("Some products failed to activate");
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedProducts.length === 0) return;
    if (
      !window.confirm(
        `Deactivate ${selectedProducts.length} selected products?`,
      )
    )
      return;

    const token = localStorage.getItem("ogestyle_token");
    try {
      await Promise.all(
        selectedProducts.map((id) =>
          axios.put(
            `${API_BASE_URL}/api/v2/products/${id}`,
            { isActive: false },
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ),
      );
      alert(`${selectedProducts.length} products deactivated!`);
      setSelectedProducts([]);
      setSelectionMode(false);
      fetchProducts(selectedBusinessId);
    } catch (error) {
      alert("Some products failed to deactivate");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) return;
    if (
      !window.confirm(
        `Delete ${selectedProducts.length} selected products? This cannot be undone!`,
      )
    )
      return;

    const token = localStorage.getItem("ogestyle_token");
    try {
      await Promise.all(
        selectedProducts.map((id) =>
          axios.delete(`${API_BASE_URL}/api/v2/products/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ),
      );
      alert(`${selectedProducts.length} products deleted!`);
      setSelectedProducts([]);
      setSelectionMode(false);
      fetchProducts(selectedBusinessId);
    } catch (error) {
      alert("Some products failed to delete");
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  };

  const selectAllProducts = () => {
    const allIds = products.map((p) => p._id);
    setSelectedProducts(allIds);
  };

  const deselectAll = () => {
    setSelectedProducts([]);
  };

  const getBulkSelectionImpact = () => {
    const selectedSet = new Set(
      (selectedProducts || []).map((id) => String(id || "").trim()),
    );
    const selectedItems = (products || []).filter((product) =>
      selectedSet.has(String(product?._id || "").trim()),
    );

    const skuRowCount = selectedItems.reduce(
      (sum, product) =>
        sum + (Array.isArray(product?.locations) ? product.locations.length : 0),
      0,
    );

    const uniqueLocationIds = new Set();
    selectedItems.forEach((product) => {
      (product?.locations || []).forEach((location) => {
        const rawShopId =
          typeof location?.shopId === "object" ? location.shopId?._id : location?.shopId;
        const normalized = String(rawShopId || "").trim();
        if (normalized) uniqueLocationIds.add(normalized);
      });
    });

    return {
      productCount: selectedItems.length,
      skuRowCount,
      locationCount: uniqueLocationIds.size,
    };
  };

  const handleBulkStockUpdate = async () => {
    if (selectedProducts.length === 0) return;

    const impact = getBulkSelectionImpact();

    const quantityStr = prompt(
      `Enter stock quantity adjustment for ${impact.productCount} products:\n\nImpact scope:\n- ${impact.skuRowCount} SKU rows\n- ${impact.locationCount} unique locations\n\nEnter a number (e.g., 10 to add 10, -5 to remove 5):`,
    );
    if (quantityStr === null) return; // User cancelled

    const quantity = parseInt(quantityStr);
    if (isNaN(quantity) || quantity === 0) {
      alert("Please enter a valid non-zero number");
      return;
    }

    const action = quantity > 0 ? "add" : "remove";
    const absQuantity = Math.abs(quantity);

    if (
      !window.confirm(
        `${action === "add" ? "Add" : "Remove"} ${absQuantity} units ${action === "add" ? "to" : "from"} ${impact.productCount} products (${impact.skuRowCount} SKU rows across ${impact.locationCount} locations)?`,
      )
    )
      return;

    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v2/products/bulk-stock-update`,
        {
          productIds: selectedProducts,
          adjustment: quantity,
          businessId: selectedBusinessId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const backendImpact = response?.data?.impact || {};
      const productsCount = backendImpact.productCount || impact.productCount;
      const skuRowsCount = backendImpact.skuRowCount || impact.skuRowCount;
      const locationsCount = backendImpact.locationCount || impact.locationCount;

      alert(
        `Successfully updated stock for ${productsCount} product(s), ${skuRowsCount} SKU row(s), across ${locationsCount} location(s).`,
      );
      setSelectedProducts([]);
      setSelectionMode(false);
      fetchProducts(selectedBusinessId);
    } catch (error) {
      alert(error.response?.data?.message || "Some products failed to update");
    }
  };

  const handleBulkPriceUpdate = async () => {
    if (selectedProducts.length === 0) return;

    const impact = getBulkSelectionImpact();

    const adjustmentType = prompt(
      "Price adjustment type:\n\n1 = Set fixed price\n2 = Percentage increase/decrease\n3 = Dollar amount increase/decrease\n\nEnter 1, 2, or 3:",
    );
    if (adjustmentType === null) return;

    const type = parseInt(adjustmentType);
    if (![1, 2, 3].includes(type)) {
      alert("Please enter 1, 2, or 3");
      return;
    }

    let value, confirmMessage;

    if (type === 1) {
      // Fixed price
      const priceStr = prompt(
        `Enter new price for all selected products (e.g., 29.99).\n\nImpact scope:\n- ${impact.productCount} products\n- ${impact.skuRowCount} SKU rows\n- ${impact.locationCount} unique locations`,
      );
      if (priceStr === null) return;
      value = parseFloat(priceStr);
      if (isNaN(value) || value < 0) {
        alert("Please enter a valid positive price");
        return;
      }
      confirmMessage = `Set price to $${value.toFixed(2)} for ${impact.productCount} products (${impact.skuRowCount} SKU rows across ${impact.locationCount} locations)?`;
    } else if (type === 2) {
      // Percentage
      const percentStr = prompt(
        "Enter percentage change (e.g., 10 for +10%, -15 for -15%):",
      );
      if (percentStr === null) return;
      value = parseFloat(percentStr);
      if (isNaN(value)) {
        alert("Please enter a valid percentage");
        return;
      }
      confirmMessage = `${value > 0 ? "Increase" : "Decrease"} prices by ${Math.abs(value)}% for ${impact.productCount} products (${impact.skuRowCount} SKU rows across ${impact.locationCount} locations)?`;
    } else {
      // Dollar amount
      const amountStr = prompt(
        "Enter dollar amount change (e.g., 5 for +$5, -3 for -$3):",
      );
      if (amountStr === null) return;
      value = parseFloat(amountStr);
      if (isNaN(value)) {
        alert("Please enter a valid amount");
        return;
      }
      confirmMessage = `${value > 0 ? "Add" : "Subtract"} $${Math.abs(value).toFixed(2)} ${value > 0 ? "to" : "from"} prices for ${impact.productCount} products (${impact.skuRowCount} SKU rows across ${impact.locationCount} locations)?`;
    }

    if (!window.confirm(confirmMessage)) return;

    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v2/products/bulk-price-update`,
        {
          productIds: selectedProducts,
          adjustmentType: type,
          value,
          businessId: selectedBusinessId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const backendImpact = response?.data?.impact || {};
      const productsCount = backendImpact.productCount || impact.productCount;
      const skuRowsCount = backendImpact.skuRowCount || impact.skuRowCount;
      const locationsCount = backendImpact.locationCount || impact.locationCount;

      alert(
        `Successfully updated prices for ${productsCount} product(s), ${skuRowsCount} SKU row(s), across ${locationsCount} location(s).`,
      );
      setSelectedProducts([]);
      setSelectionMode(false);
      fetchProducts(selectedBusinessId);
    } catch (error) {
      alert(error.response?.data?.message || "Some products failed to update");
    }
  };

  const getStockStatusColor = (stock, threshold = 5) => {
    if (stock === 0) return "#ef4444"; // Red - Out of stock
    if (stock <= threshold) return "#f59e0b"; // Amber - Low stock
    return "#10b981"; // Green - In stock
  };

  const getStockStatusText = (stock, threshold = 5) => {
    if (stock === 0) return "Out of Stock";
    if (stock <= threshold) return "Low Stock";
    return "In Stock";
  };

  const getProductLocationCount = (product) => {
    const uniqueShopIds = new Set(
      (product?.locations || [])
        .map((location) => {
          const rawShopId =
            typeof location?.shopId === "object" ? location.shopId?._id : location?.shopId;
          return String(rawShopId || "").trim();
        })
        .filter(Boolean),
    );
    return uniqueShopIds.size;
  };

  const getProductVariantCount = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const meaningfulVariants = variants.filter((variant) => {
      const key = String(variant?.variantKey || "default").trim().toLowerCase();
      return key !== "default";
    });
    return meaningfulVariants.length > 0 ? meaningfulVariants.length : variants.length;
  };

  const buildInventoryGroupsByLocation = (product) => {
    const grouped = new Map();
    const locations = Array.isArray(product?.locations) ? product.locations : [];

    locations.forEach((entry, index) => {
      const rawShopId =
        typeof entry?.shopId === "object" ? entry.shopId?._id : entry?.shopId;
      const shopId = String(rawShopId || "unknown").trim() || `unknown-${index}`;
      const shopName =
        (typeof entry?.shopId === "object" && entry.shopId?.name) ||
        entry?.shopName ||
        "Unknown Location";
      const city =
        (typeof entry?.shopId === "object" && entry.shopId?.city) ||
        entry?.city ||
        "";
      const state =
        (typeof entry?.shopId === "object" && entry.shopId?.state) ||
        entry?.state ||
        "";

      if (!grouped.has(shopId)) {
        grouped.set(shopId, {
          shopId,
          shopName,
          city,
          state,
          totalStock: 0,
          totalSold: 0,
          variants: [],
        });
      }

      const target = grouped.get(shopId);
      const stock = parseInt(entry?.stock, 10) || 0;
      const sold = parseInt(entry?.sold, 10) || 0;
      const variantKey = String(entry?.variantKey || "default").trim() || "default";
      const variantLabel =
        String(entry?.variantLabel || "").trim() ||
        (variantKey === "default" ? "Default" : variantKey);

      target.totalStock += stock;
      target.totalSold += sold;
      target.variants.push({
        _id: entry?._id,
        stock,
        sold,
        price:
          entry?.price !== undefined && entry?.price !== null
            ? Number(entry.price)
            : Number(product?.basePrice || 0),
        isActive: entry?.isActive !== false,
        variantKey,
        variantLabel,
        variantSku: entry?.variantSku || "",
      });
    });

    return Array.from(grouped.values());
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      basePrice: "",
      baseStock: "",
      category: [],
      brand: "",
      sku: "",
      lowStockThreshold: 5,
      images: [],
      isFeatured: false,
      recommendedForServices: [],
    });
    setLocationInventory([
      { shopId: "", quantity: 0, price: "", useMasterPrice: true },
    ]);
    setUseSamePriceForAll(true);
    setCreationMode("single"); // Reset to single-variant path
    setHasVariants(false); // Single path starts with no variants
    setVariantDimensions([]);
    setVariants([]);
    setImageFile(null);
    setImagePreview(null);
    setUploadingImage(false);

    // Clear file input
    const fileInput = document.getElementById("productImageInput");
    if (fileInput) fileInput.value = "";
  };

  // Get list of already selected shop IDs
  const getSelectedShopIds = () => {
    return locationInventory.map((loc) => loc.shopId).filter(Boolean);
  };

  const filteredProducts =
    selectedCategory === "all"
      ? products
      : products.filter(
          (p) => p.category && p.category.includes(selectedCategory),
        );

  if (loading) {
    return (
      <SidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div>Loading products...</div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div
        style={{
          padding: isMobile ? "12px" : "18px",
          width: "100%",
          maxWidth: "none",
          margin: 0,
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            marginBottom: "24px",
            gap: isMobile ? "16px" : "0",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: isMobile ? "17px" : "20px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "4px",
              }}
            >
              🛍️ Products & Inventory
            </h1>
            <p
              style={{ color: "#6b7280", fontSize: isMobile ? "11px" : "12px" }}
            >
              Manage products across all locations with inventory tracking
            </p>
          </div>

          {businesses.length > 1 && (
            <BusinessSelector
              businesses={businesses}
              selectedBusinessId={selectedBusinessId}
              onChange={handleBusinessChange}
            />
          )}
        </div>

        {setupUiEnabled && (
          <StorefrontSetupStatusBanner
            statusData={storefrontStatus}
            isMobile={isMobile}
            setupPath="/dashboard/products/storefront-setup"
          />
        )}

        {/* Add Product Button */}
        <div
          style={{
            marginBottom: "24px",
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          {!selectionMode ? (
            <>
              <button
                onClick={() => setSelectionMode(true)}
                style={{
                  padding: isMobile ? "10px 16px" : "12px 24px",
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : "14px",
                  flex: isMobile ? "1 1 45%" : "0 0 auto",
                }}
              >
                ☑️ Select Products
              </button>
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                style={{
                  padding: isMobile ? "10px 16px" : "12px 24px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : "14px",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  flex: isMobile ? "1 1 45%" : "0 0 auto",
                }}
              >
                + Add New Product
              </button>
            </>
          ) : (
            <>
              <span
                style={{
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  background: "#eff6ff",
                  color: "#1e40af",
                  borderRadius: "12px",
                  fontSize: isMobile ? "13px" : "14px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {selectedProducts.length} selected
              </span>
              <button
                onClick={selectAllProducts}
                style={{
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                Select All
              </button>
              <button
                onClick={deselectAll}
                style={{
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                Clear
              </button>
              <button
                onClick={handleBulkActivate}
                disabled={selectedProducts.length === 0}
                style={{
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  background:
                    selectedProducts.length === 0 ? "#d1d5db" : "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor:
                    selectedProducts.length === 0 ? "not-allowed" : "pointer",
                  fontWeight: "500",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                ✓ Activate
              </button>
              <button
                onClick={handleBulkDeactivate}
                disabled={selectedProducts.length === 0}
                style={{
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  background:
                    selectedProducts.length === 0 ? "#d1d5db" : "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor:
                    selectedProducts.length === 0 ? "not-allowed" : "pointer",
                  fontWeight: "500",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                ⏸ Deactivate
              </button>
              <button
                onClick={handleBulkStockUpdate}
                disabled={selectedProducts.length === 0}
                style={{
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  background:
                    selectedProducts.length === 0 ? "#d1d5db" : "#8b5cf6",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor:
                    selectedProducts.length === 0 ? "not-allowed" : "pointer",
                  fontWeight: "500",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                📦 Update Stock (All Variants)
              </button>
              <button
                onClick={handleBulkPriceUpdate}
                disabled={selectedProducts.length === 0}
                style={{
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  background:
                    selectedProducts.length === 0 ? "#d1d5db" : "#f59e0b",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor:
                    selectedProducts.length === 0 ? "not-allowed" : "pointer",
                  fontWeight: "500",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                💰 Update Prices (All Variants)
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={selectedProducts.length === 0}
                style={{
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  background:
                    selectedProducts.length === 0 ? "#d1d5db" : "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor:
                    selectedProducts.length === 0 ? "not-allowed" : "pointer",
                  fontWeight: "500",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                🗑️ Delete
              </button>
              <button
                onClick={() => {
                  setSelectionMode(false);
                  setSelectedProducts([]);
                }}
                style={{
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                Cancel
              </button>
              <div
                style={{
                  width: "100%",
                  fontSize: "12px",
                  color: "#4b5563",
                }}
              >
                Bulk actions apply to all SKU rows for each selected product
                (single: default row, multi-variant: every variant across all
                locations).
              </div>
            </>
          )}
        </div>

        {/* Low Stock Alert Banner */}
        {(() => {
          // Calculate low stock products across all locations
          const lowStockItems = [];

          products.forEach((product) => {
            if (product.locations && product.locations.length > 0) {
              product.locations.forEach((location) => {
                const stock = location.stock || 0;
                const threshold = product.lowStockThreshold || 5;

                if (stock <= threshold) {
                  lowStockItems.push({
                    productId: product._id,
                    productName: product.name,
                    stock: stock,
                    threshold: threshold,
                    shopName: location.shopId?.name || "Unknown Shop",
                    shopId: location.shopId?._id,
                  });
                }
              });
            }
          });

          if (lowStockItems.length === 0) return null;

          return (
            <div
              style={{
                background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                border: "2px solid #f59e0b",
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "24px",
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
              }}
            >
              <span style={{ fontSize: "24px" }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <h4
                  style={{
                    margin: "0 0 8px",
                    color: "#92400e",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  Low Stock Alert
                </h4>
                <p
                  style={{
                    margin: "0 0 12px",
                    color: "#78350f",
                    fontSize: "14px",
                  }}
                >
                  {lowStockItems.length}{" "}
                  {lowStockItems.length === 1 ? "item needs" : "items need"}{" "}
                  restocking across{" "}
                  {new Set(lowStockItems.map((i) => i.shopId)).size}{" "}
                  {new Set(lowStockItems.map((i) => i.shopId)).size === 1
                    ? "location"
                    : "locations"}
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {lowStockItems.map((item, index) => (
                    <div
                      key={`${item.productId}-${item.shopId}-${index}`}
                      style={{
                        background: "white",
                        border: "1px solid #f59e0b",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        fontSize: "13px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <span style={{ fontWeight: "600", color: "#92400e" }}>
                        {item.productName}
                      </span>
                      <span style={{ fontSize: "11px", color: "#78350f" }}>
                        {item.shopName}
                      </span>
                      <span
                        style={{
                          background: item.stock === 0 ? "#fee2e2" : "#fef3c7",
                          color: item.stock === 0 ? "#991b1b" : "#92400e",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: "600",
                          alignSelf: "flex-start",
                        }}
                      >
                        {item.stock === 0
                          ? "Out of Stock"
                          : `${item.stock} left`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Products Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fill, minmax(250px, 1fr))",
            gap: isMobile ? "12px" : "14px",
            marginTop: "14px",
          }}
        >
          {filteredProducts.length === 0 ? (
            <div
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                padding: "60px 20px",
                background: "#f9fafb",
                borderRadius: "12px",
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📦</div>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "8px",
                }}
              >
                No Products Yet
              </h3>
              <p style={{ color: "#6b7280", fontSize: "14px" }}>
                Add your first product to start selling
              </p>
            </div>
          ) : (
            filteredProducts.map((product) => (
              <div
                key={product._id}
                style={{
                  background: "white",
                  borderRadius: "10px",
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  position: "relative",
                  cursor: selectionMode ? "pointer" : "default",
                }}
                onClick={() =>
                  selectionMode && toggleProductSelection(product._id)
                }
                onMouseEnter={(e) => {
                  if (!selectionMode) {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 24px rgba(0, 0, 0, 0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selectionMode) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(0, 0, 0, 0.1)";
                  }
                }}
              >
                {/* Selection Checkbox */}
                {selectionMode && (
                  <div
                    style={{
                      position: "absolute",
                      top: "12px",
                      left: "12px",
                      zIndex: 10,
                      width: "24px",
                      height: "24px",
                      borderRadius: "6px",
                      background: selectedProducts.includes(product._id)
                        ? "#10b981"
                        : "white",
                      border: `2px solid ${selectedProducts.includes(product._id) ? "#10b981" : "#d1d5db"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    {selectedProducts.includes(product._id) && (
                      <span
                        style={{
                          color: "white",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </div>
                )}

                {/* Product Image */}
                {product.images && product.images.length > 0 && (
                  <div
                    style={{
                      width: "100%",
                      height: isMobile ? "120px" : "140px",
                      background: `url(${product.images[0]}) center/cover`,
                      position: "relative",
                    }}
                  >
                    {product.isFeatured && (
                      <div
                        style={{
                          position: "absolute",
                          top: "12px",
                          right: "12px",
                          background: "#fbbf24",
                          color: "white",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        ⭐ Featured
                      </div>
                    )}
                    {getProductVariantCount(product) > 1 && (
                      <div
                        style={{
                          position: "absolute",
                          top: product.isFeatured ? "42px" : "12px",
                          right: "12px",
                          background: "#dbeafe",
                          color: "#1d4ed8",
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "11px",
                          fontWeight: "600",
                          border: "1px solid #93c5fd",
                        }}
                      >
                        {getProductVariantCount(product)} variants
                      </div>
                    )}
                  </div>
                )}

                <div style={{ padding: isMobile ? "16px" : "20px" }}>
                  {/* Product Name */}
                  <h3
                    style={{
                      fontSize: isMobile ? "14px" : "15px",
                      fontWeight: "600",
                      marginBottom: "6px",
                      color: "#1a1a1a",
                    }}
                  >
                    {product.name}
                  </h3>

                  {/* Brand */}
                  {product.brand && (
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#6b7280",
                        marginBottom: "8px",
                      }}
                    >
                      by {product.brand}
                    </p>
                  )}

                  {/* Price */}
                  <div
                    style={{
                      fontSize: isMobile ? "16px" : "18px",
                      fontWeight: "700",
                      color: "#10b981",
                      marginBottom: "8px",
                    }}
                  >
                    From ${product.basePrice.toFixed(2)}
                  </div>

                  {/* Inventory Summary */}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginBottom: "10px",
                      fontSize: "10px",
                      color: "#6b7280",
                    }}
                  >
                    <div>
                      📍 {getProductLocationCount(product)} location
                      {getProductLocationCount(product) !== 1 ? "s" : ""}
                    </div>
                    <div>📦 {product.totalStock || 0} in stock</div>
                    <div>💰 {product.totalSold || 0} sold</div>
                  </div>

                  {/* Actions */}
                  {!selectionMode && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr 1fr"
                          : "1fr 1fr 1fr",
                        gap: "8px",
                      }}
                    >
                      <button
                        onClick={() => handleViewInventory(product)}
                        style={{
                          gridColumn: isMobile ? "1 / -1" : "auto",
                          padding: "6px",
                          background: "#10b981",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "11px",
                          fontWeight: "500",
                        }}
                      >
                        📊 Manage Inventory
                      </button>
                      <button
                        onClick={() => handleEdit(product)}
                        style={{
                          padding: "6px",
                          background: "#3b82f6",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "11px",
                          fontWeight: "500",
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(product._id)}
                        style={{
                          padding: "6px",
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "11px",
                          fontWeight: "500",
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Product Form Modal - CONTINUED IN NEXT PART */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: isMobile ? "16px" : "20px",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: isMobile ? "20px" : "32px",
                width: "min(1280px, 96vw)",
                maxWidth: "1280px",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              }}
            >
              <h2
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  marginBottom: "24px",
                  color: "#1a1a1a",
                }}
              >
                {editingProduct ? "✏️ Edit Product" : "➕ Add New Product"}
              </h2>

              <form onSubmit={handleSubmit}>
                {/* Product Details Section */}
                <div
                  style={{
                    background: "#f9fafb",
                    padding: isMobile ? "16px" : "20px",
                    borderRadius: "12px",
                    marginBottom: "24px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      marginBottom: "16px",
                      color: "#374151",
                    }}
                  >
                    📝 Product Information
                  </h3>

                  {/* Product Name */}
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Product Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      placeholder="e.g., Color WOW Hair Treatment"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Description *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      required
                      minLength={30}
                      placeholder="Describe your product..."
                      rows="3"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        resize: "vertical",
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                      }}
                    />
                    <p
                      style={{
                        margin: "6px 0 0",
                        fontSize: "12px",
                        color:
                          trimmedDescriptionLength >= 30 ? "#059669" : "#b45309",
                      }}
                    >
                      {trimmedDescriptionLength >= 30
                        ? `Minimum met (${trimmedDescriptionLength}/30 characters)`
                        : `${30 - trimmedDescriptionLength} more characters required (minimum 30)`}
                    </p>
                  </div>

                  {/* Image Upload */}
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      {`Product Images (up to ${maxProductImagesPerProduct})`}
                    </label>

                    {/* File input */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: "12px",
                        alignItems: isMobile ? "stretch" : "center",
                      }}
                    >
                      <ImageUploadField
                        multiple
                        variant="inline"
                        buttonLabel={`Choose up to ${maxProductImagesPerProduct} images`}
                        showHint={false}
                        showPreviews={false}
                        max={maxProductImagesPerProduct}
                        endpoint={`${API_BASE_URL}/api/v2/products/images`}
                        fieldName="images"
                        onChange={handleImagesUploaded}
                        onError={(message) => alert(message)}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        marginTop: "4px",
                      }}
                    >
                      {Array.isArray(formData.images) && formData.images.length > 0
                        ? `${formData.images.length} of ${maxProductImagesPerProduct} image(s) selected`
                        : `Upload up to ${maxProductImagesPerProduct} product images`}
                      {!uploadingImage && (
                        <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "4px" }}>
                          {IMAGE_UPLOAD_HINT}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Category Selection (Checkboxes - one or more categories) */}
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Product Categories (Select one or more) *
                    </label>
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#6b7280",
                        marginBottom: "12px",
                        marginTop: "4px",
                      }}
                    >
                      Select categories that best describe your product
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "repeat(auto-fill, minmax(180px, 1fr))",
                        gap: "12px",
                        padding: "16px",
                        background: "#f9fafb",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      {categoryOptionsForForm.map((cat) => {
                        const isChecked = formData.category.includes(cat.value);

                        return (
                          <label
                            key={cat.value}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "12px",
                              background: isChecked ? "#dbeafe" : "white",
                              border: isChecked
                                ? "2px solid #3b82f6"
                                : "1px solid #d1d5db",
                              borderRadius: "8px",
                              cursor: "pointer",
                              opacity: 1,
                              transition: "all 0.2s ease",
                              boxSizing: "border-box",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const newCategories = e.target.checked
                                  ? [...formData.category, cat.value]
                                  : formData.category.filter(
                                      (c) => c !== cat.value,
                                    );
                                setFormData({
                                  ...formData,
                                  category: newCategories,
                                });
                              }}
                              style={{
                                marginRight: "8px",
                                cursor: "pointer",
                                width: "16px",
                                height: "16px",
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                fontSize: "14px",
                                color: "#374151",
                                fontWeight: isChecked ? "600" : "400",
                              }}
                            >
                              {cat.icon && `${cat.icon} `}
                              {cat.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {setupPrimaryCategorySet.size > 0 && (
                      <p
                        style={{
                          color: "#1d4ed8",
                          fontSize: "12px",
                          marginTop: "8px",
                        }}
                      >
                        Available categories are scoped by your storefront
                        setup.
                      </p>
                    )}
                    {formData.category.length === 0 && (
                      <p
                        style={{
                          color: "#ef4444",
                          fontSize: "13px",
                          marginTop: "8px",
                          fontWeight: "500",
                        }}
                      >
                        ⚠️ Please select at least one category
                      </p>
                    )}
                    {formData.category.length > 0 && (
                      <p
                        style={{
                          color: "#10b981",
                          fontSize: "13px",
                          marginTop: "8px",
                          fontWeight: "500",
                        }}
                      >
                        ✓ {formData.category.length} categor
                        {formData.category.length === 1 ? "y" : "ies"} selected
                      </p>
                    )}
                  </div>

                  {/* Brand & SKU Row */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                      gap: "16px",
                      marginBottom: "16px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#374151",
                        }}
                      >
                        Brand
                      </label>
                      <input
                        type="text"
                        value={formData.brand}
                        onChange={(e) =>
                          setFormData({ ...formData, brand: e.target.value })
                        }
                        placeholder="e.g., Color WOW"
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#374151",
                        }}
                      >
                        SKU Prefix (Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.sku}
                        onChange={(e) =>
                          setFormData({ ...formData, sku: e.target.value })
                        }
                        placeholder="e.g., HC-CRM"
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          boxSizing: "border-box",
                        }}
                      />
                      <p
                        style={{
                          margin: "6px 0 0",
                          fontSize: "11px",
                          color: "#6b7280",
                        }}
                      >
                        Used to auto-fill missing variant SKUs like {formData.sku || "PREFIX"}-VOLUME-10ML.
                      </p>
                    </div>
                  </div>

                  {/* Featured Checkbox */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "12px",
                      background: "#fef3c7",
                      borderRadius: "8px",
                      border: "1px solid #fbbf24",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.isFeatured}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isFeatured: e.target.checked,
                        })
                      }
                      id="featured"
                    />
                    <label
                      htmlFor="featured"
                      style={{
                        fontSize: "14px",
                        cursor: "pointer",
                        fontWeight: "500",
                      }}
                    >
                      ⭐ Feature this product on homepage
                    </label>
                  </div>
                </div>

                {/* Phase C: Variant Creation Mode Selector */}
                <div
                  style={{
                    background: "#f0f9ff",
                    padding: "20px",
                    borderRadius: "12px",
                    marginBottom: "24px",
                    border: "2px solid #0284c7",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      marginBottom: "12px",
                      color: "#0c4a6e",
                    }}
                  >
                    🎯 How many options does this product have?
                  </h3>
                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => setCreationMode("single")}
                      style={{
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border:
                          creationMode === "single"
                            ? "2px solid #0284c7"
                            : "1px solid #cbd5e1",
                        background:
                          creationMode === "single" ? "#e0f2fe" : "white",
                        color:
                          creationMode === "single" ? "#0c4a6e" : "#475569",
                        fontWeight: creationMode === "single" ? "600" : "500",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      📦 Single Variant (one option set)
                    </button>
                    <button
                      type="button"
                      onClick={() => setCreationMode("multi")}
                      style={{
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border:
                          creationMode === "multi"
                            ? "2px solid #0284c7"
                            : "1px solid #cbd5e1",
                        background:
                          creationMode === "multi" ? "#e0f2fe" : "white",
                        color:
                          creationMode === "multi" ? "#0c4a6e" : "#475569",
                        fontWeight: creationMode === "multi" ? "600" : "500",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      🏷️ Multiple Variants (Size, Color, etc.)
                    </button>
                  </div>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#0c4a6e",
                      marginTop: "12px",
                      lineHeight: "1.5",
                    }}
                  >
                    {creationMode === "single"
                      ? "This product will be created with a single option set using the base price and stock above. You can add variants later by editing the product."
                      : "Use this for products with multiple options like Size, Volume, Color, or Scent. You'll define dimensions and generate a matrix."}
                  </p>
                </div>

                {/* Low Stock Alert Threshold */}
                <div
                  style={{
                    background: "#fef3c7",
                    padding: isMobile ? "16px" : "20px",
                    borderRadius: "12px",
                    marginBottom: "24px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      marginBottom: "16px",
                      color: "#374151",
                    }}
                  >
                    ⚠️ Low Stock Alert
                  </h3>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#374151",
                        }}
                      >
                        Base Price *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.basePrice}
                        onChange={(e) =>
                          setFormData({ ...formData, basePrice: e.target.value })
                        }
                        required={creationMode === "single"}
                        placeholder="0.00"
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#374151",
                        }}
                      >
                        Base Stock *
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.baseStock}
                        onChange={(e) =>
                          setFormData({ ...formData, baseStock: e.target.value })
                        }
                        required={creationMode === "single"}
                        placeholder="0"
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#374151",
                        }}
                      >
                        Alert Threshold
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.lowStockThreshold}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            lowStockThreshold: e.target.value,
                          })
                        }
                        placeholder="5"
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "8px",
                    }}
                  >
                    {creationMode === "single"
                      ? "Customers see base price and stock for single-option products."
                      : "Base price and stock are defaults for generated variants; adjust per variant as needed."}
                  </p>
                </div>

                {/* Phase C: Variant Editor or Single Variant Path */}
                {creationMode === "multi" ? (
                  <>
                    {/* Preset Dimensions Shortcuts (multi-variant only) */}
                    <div
                      style={{
                        background: "#f3e8ff",
                        padding: isMobile ? "16px" : "20px",
                        borderRadius: "12px",
                        marginBottom: "24px",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          marginBottom: "12px",
                          color: "#6b21a8",
                        }}
                      >
                        💡 Quick Start: Choose preset dimensions
                      </h3>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {Object.entries(PRESET_DIMENSIONS).map(([key, preset]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              const newDimensions = [
                                ...variantDimensions,
                                {
                                  name: preset.name,
                                  key: preset.key,
                                  position: variantDimensions.length,
                                  values: preset.values.map((v, idx) => ({
                                    ...v,
                                    position: idx,
                                    isActive: true,
                                  })),
                                },
                              ];
                              setVariantDimensions(newDimensions);
                            }}
                            disabled={variantDimensions.some(
                              (d) => d.key === preset.key,
                            )}
                            style={{
                              padding: "8px 12px",
                              background: variantDimensions.some(
                                (d) => d.key === preset.key,
                              )
                                ? "#e9d5ff"
                                : "white",
                              border: "1px solid #d8b4fe",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "500",
                              cursor: variantDimensions.some(
                                (d) => d.key === preset.key,
                              )
                                ? "not-allowed"
                                : "pointer",
                              color: variantDimensions.some(
                                (d) => d.key === preset.key,
                              )
                                ? "#7c3aed"
                                : "#6b21a8",
                            }}
                          >
                            {preset.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    <VariantEditor
                      hasVariants={true}
                      variantDimensions={variantDimensions}
                      variants={variants}
                      basePrice={parseFloat(formData.basePrice) || 0}
                      baseStock={parseInt(formData.baseStock, 10) || 0}
                      skuPrefix={formData.sku}
                      isMobile={isMobile}
                      onVariantsChange={setVariants}
                      onDimensionsChange={setVariantDimensions}
                      onHasVariantsChange={setHasVariants}
                      lockVariantMode={true}
                    />
                  </>
                  ) : null}

                {/* Inventory by Location Section */}
                <div
                  style={{
                    background: "#eff6ff",
                    padding: isMobile ? "16px" : "20px",
                    borderRadius: "12px",
                    marginBottom: "24px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      📦 Inventory by Location
                    </h3>
                    <button
                      type="button"
                      onClick={addLocationRow}
                      disabled={locationInventory.length >= availableShops.length}
                      style={{
                        padding: "6px 12px",
                        background:
                          locationInventory.length >= availableShops.length
                            ? "#9ca3af"
                            : "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor:
                          locationInventory.length >= availableShops.length
                            ? "not-allowed"
                            : "pointer",
                        fontSize: "12px",
                        fontWeight: "500",
                      }}
                    >
                      + Add Location{" "}
                      {locationInventory.length >= availableShops.length &&
                        "(All used)"}
                    </button>
                  </div>

                  {locationInventory.map((loc, index) => (
                    <div
                      key={index}
                      style={{
                        background: "white",
                        padding: isMobile ? "12px" : "16px",
                        borderRadius: "8px",
                        marginBottom: "12px",
                        border: "1px solid #dbeafe",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "12px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#374151",
                          }}
                        >
                          Location {index + 1}
                        </span>
                        {locationInventory.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLocationRow(index)}
                            style={{
                              padding: "4px 8px",
                              background: "#fee2e2",
                              color: "#dc2626",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "11px",
                              fontWeight: "500",
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                          gap: "12px",
                        }}
                      >
                        <div style={{ gridColumn: isMobile ? "1" : "1 / 2" }}>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "6px",
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#374151",
                            }}
                          >
                            Shop Location *
                          </label>
                          <select
                            value={loc.shopId}
                            onChange={(e) =>
                              updateLocationRow(index, "shopId", e.target.value)
                            }
                            required
                            style={{
                              width: "100%",
                              padding: "8px 10px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "13px",
                              boxSizing: "border-box",
                            }}
                          >
                            <option value="">Select location...</option>
                            {availableShops.map((shop) => {
                              const selectedShopIds = getSelectedShopIds();
                              const isUsed =
                                selectedShopIds.includes(shop._id) &&
                                loc.shopId !== shop._id;
                              return (
                                <option key={shop._id} value={shop._id} disabled={isUsed}>
                                  {shop.name} - {shop.city}, {shop.state}
                                  {isUsed ? " (already added)" : ""}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "6px",
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#374151",
                            }}
                          >
                            Stock Source
                          </label>
                          <div
                            style={{
                              width: "100%",
                              padding: "8px 10px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "13px",
                              boxSizing: "border-box",
                              background: "#f9fafb",
                              color: "#4b5563",
                            }}
                          >
                            {hasVariants
                              ? "Base stock and variant-level stock"
                              : "Base stock level"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "12px",
                      padding: "8px",
                      background: "white",
                      borderRadius: "6px",
                    }}
                  >
                    💡 Tip: Add as many locations as you need. Each location maintains its own inventory.
                  </div>
                </div>

                {/* Form Actions */}
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    flexDirection: isMobile ? "column" : "row",
                  }}
                >
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: isSubmitting ? "#9ca3af" : "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    {isSubmitting
                      ? "Saving..."
                      : editingProduct
                        ? "✅ Update Product"
                        : "➕ Create Product"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingProduct(null);
                      resetForm();
                    }}
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: "#6b7280",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Inventory Management Modal */}
        {showInventoryModal && selectedProductForInventory && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1001,
              padding: isMobile ? "16px" : "20px",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: isMobile ? "20px" : "32px",
                maxWidth: "900px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "24px",
                }}
              >
                <div>
                  <h2
                    style={{
                      fontSize: isMobile ? "20px" : "24px",
                      fontWeight: "700",
                      marginBottom: "8px",
                      color: "#1a1a1a",
                    }}
                  >
                    📊 Inventory Management
                  </h2>
                  <h3
                    style={{
                      fontSize: isMobile ? "16px" : "18px",
                      fontWeight: "600",
                      color: "#6b7280",
                    }}
                  >
                    {selectedProductForInventory.name}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowInventoryModal(false);
                    setSelectedProductForInventory(null);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "28px",
                    cursor: "pointer",
                    color: "#6b7280",
                    lineHeight: "1",
                  }}
                >
                  ×
                </button>
              </div>

              {/* Business-Level Summary */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  padding: isMobile ? "16px" : "20px",
                  borderRadius: "12px",
                  marginBottom: "24px",
                  color: "white",
                }}
              >
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "12px",
                    opacity: 0.9,
                  }}
                >
                  BUSINESS-WIDE TOTALS
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "repeat(2, 1fr)"
                      : "repeat(4, 1fr)",
                    gap: isMobile ? "12px" : "16px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: isMobile ? "11px" : "12px",
                        opacity: 0.8,
                        marginBottom: "4px",
                      }}
                    >
                      Total Locations
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? "20px" : "24px",
                        fontWeight: "700",
                      }}
                    >
                      {buildInventoryGroupsByLocation(selectedProductForInventory)
                        .length || 0}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: isMobile ? "11px" : "12px",
                        opacity: 0.8,
                        marginBottom: "4px",
                      }}
                    >
                      Total Stock
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? "20px" : "24px",
                        fontWeight: "700",
                      }}
                    >
                      {selectedProductForInventory.totalStock || 0}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: isMobile ? "11px" : "12px",
                        opacity: 0.8,
                        marginBottom: "4px",
                      }}
                    >
                      Total Sold
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? "20px" : "24px",
                        fontWeight: "700",
                      }}
                    >
                      {selectedProductForInventory.totalSold || 0}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: isMobile ? "11px" : "12px",
                        opacity: 0.8,
                        marginBottom: "4px",
                      }}
                    >
                      Base Price
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? "20px" : "24px",
                        fontWeight: "700",
                      }}
                    >
                      ${selectedProductForInventory.basePrice.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Location-Wise Inventory */}
              <h4
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "16px",
                  color: "#374151",
                }}
              >
                📍 Inventory by Location
              </h4>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "16px",
                }}
              >
                {selectedProductForInventory.locations &&
                selectedProductForInventory.locations.length > 0 ? (
                  buildInventoryGroupsByLocation(selectedProductForInventory).map(
                    (locationGroup, index) => {
                      const stockStatus = getStockStatusText(
                        locationGroup.totalStock || 0,
                        selectedProductForInventory.lowStockThreshold,
                      );
                      const statusColor = getStockStatusColor(
                        locationGroup.totalStock || 0,
                        selectedProductForInventory.lowStockThreshold,
                      );

                      return (
                        <div
                          key={locationGroup.shopId || index}
                          style={{
                            background: "#f9fafb",
                            padding: isMobile ? "16px" : "20px",
                            borderRadius: "12px",
                            border: "2px solid #e5e7eb",
                          }}
                        >
                          {/* Location Header */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "16px",
                              flexWrap: "wrap",
                              gap: "12px",
                            }}
                          >
                            <div style={{ flex: 1, minWidth: "200px" }}>
                              <h5
                                style={{
                                  fontSize: isMobile ? "15px" : "16px",
                                  fontWeight: "600",
                                  marginBottom: "4px",
                                  color: "#1a1a1a",
                                }}
                              >
                                {locationGroup.shopName || "Unknown Location"}
                              </h5>
                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "#6b7280",
                                }}
                              >
                                {locationGroup.city}, {locationGroup.state}
                              </p>
                            </div>
                            <div
                              style={{
                                padding: "6px 12px",
                                background: statusColor,
                                color: "white",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: "600",
                              }}
                            >
                              {stockStatus}
                            </div>
                          </div>

                          {/* Location Totals */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: isMobile
                                ? "repeat(2, 1fr)"
                                : "repeat(4, 1fr)",
                              gap: "12px",
                              marginBottom: "16px",
                            }}
                          >
                            <div
                              style={{
                                background: "white",
                                padding: "12px",
                                borderRadius: "8px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  marginBottom: "4px",
                                }}
                              >
                                In Stock
                              </div>
                              <div
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "700",
                                  color: "#1a1a1a",
                                }}
                              >
                                {locationGroup.totalStock || 0}
                              </div>
                            </div>
                            <div
                              style={{
                                background: "white",
                                padding: "12px",
                                borderRadius: "8px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  marginBottom: "4px",
                                }}
                              >
                                Sold
                              </div>
                              <div
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "700",
                                  color: "#10b981",
                                }}
                              >
                                {locationGroup.totalSold || 0}
                              </div>
                            </div>
                            <div
                              style={{
                                background: "white",
                                padding: "12px",
                                borderRadius: "8px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  marginBottom: "4px",
                                }}
                              >
                                Variants
                              </div>
                              <div
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "700",
                                  color: "#3b82f6",
                                }}
                              >
                                {locationGroup.variants.length} variant
                                {locationGroup.variants.length === 1 ? "" : "s"}
                              </div>
                            </div>
                            <div
                              style={{
                                background: "white",
                                padding: "12px",
                                borderRadius: "8px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  marginBottom: "4px",
                                }}
                              >
                                Status
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: "#10b981",
                                }}
                              >
                                ✓ Active
                              </div>
                            </div>
                          </div>

                          {/* Variant Rows */}
                          <div
                            style={{
                              display: "grid",
                              gap: "10px",
                            }}
                          >
                            {locationGroup.variants.map((variantRow, variantIndex) => (
                              <div
                                key={`${locationGroup.shopId}-${variantRow._id || variantIndex}`}
                                style={{
                                  background: "white",
                                  borderRadius: "10px",
                                  border: "1px solid #e5e7eb",
                                  padding: "12px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "8px",
                                    marginBottom: "8px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#111827" }}>
                                    {variantRow.variantLabel || variantRow.variantKey || "Default"}
                                  </div>
                                  {variantRow.variantSku && (
                                    <div style={{ fontSize: "11px", color: "#4b5563" }}>
                                      SKU: {variantRow.variantSku}
                                    </div>
                                  )}
                                </div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
                                    gap: "8px",
                                    marginBottom: "10px",
                                  }}
                                >
                                  <div style={{ fontSize: "12px", color: "#374151" }}>In Stock: <strong>{variantRow.stock}</strong></div>
                                  <div style={{ fontSize: "12px", color: "#374151" }}>Sold: <strong>{variantRow.sold}</strong></div>
                                  <div style={{ fontSize: "12px", color: "#374151" }}>Price: <strong>${Number.isFinite(variantRow.price) ? variantRow.price.toFixed(2) : Number(selectedProductForInventory.basePrice || 0).toFixed(2)}</strong></div>
                                  <div style={{ fontSize: "12px", color: variantRow.isActive ? "#10b981" : "#ef4444" }}>
                                    {variantRow.isActive ? "✓ Active" : "✗ Inactive"}
                                  </div>
                                </div>
                                <button
                                  onClick={() =>
                                    handleRestockClick(
                                      {
                                        _id: variantRow._id,
                                        shopId: {
                                          _id: locationGroup.shopId,
                                          name: locationGroup.shopName,
                                        },
                                        variantLabel: variantRow.variantLabel,
                                      },
                                      selectedProductForInventory.name,
                                    )
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "8px",
                                    background: "#10b981",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                  }}
                                >
                                  📦 Restock {variantRow.variantLabel || "Variant"}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    },
                  )
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: "#6b7280",
                    }}
                  >
                    No locations found for this product
                  </div>
                )}
              </div>

              {/* Close Button */}
              <div style={{ marginTop: "24px" }}>
                <button
                  onClick={() => {
                    setShowInventoryModal(false);
                    setSelectedProductForInventory(null);
                  }}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "#6b7280",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Restock Modal */}
        {showRestockModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1002,
              padding: isMobile ? "16px" : "20px",
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: isMobile ? "20px" : "32px",
                maxWidth: "500px",
                width: "100%",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              }}
            >
              <h2
                style={{
                  fontSize: isMobile ? "18px" : "20px",
                  fontWeight: "700",
                  marginBottom: "8px",
                  color: "#1a1a1a",
                }}
              >
                📦 Restock Inventory
              </h2>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                  marginBottom: "24px",
                }}
              >
                {restockData.shopName}
                {restockData.variantLabel ? ` • ${restockData.variantLabel}` : ""}
              </p>

              <form onSubmit={handleRestockSubmit}>
                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                    }}
                  >
                    Quantity to Add *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={restockData.quantity}
                    onChange={(e) =>
                      setRestockData({
                        ...restockData,
                        quantity: e.target.value,
                      })
                    }
                    required
                    placeholder="Enter quantity"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "2px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "16px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                    }}
                  >
                    Notes (Optional)
                  </label>
                  <textarea
                    value={restockData.notes}
                    onChange={(e) =>
                      setRestockData({ ...restockData, notes: e.target.value })
                    }
                    placeholder="e.g., Supplier: XYZ, PO#: 12345"
                    rows="3"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                      resize: "vertical",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    flexDirection: isMobile ? "column" : "row",
                  }}
                >
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: isSubmitting ? "#9ca3af" : "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    {isSubmitting ? "Adding..." : "✅ Add Stock"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowRestockModal(false);
                      setRestockData({
                        shopProductId: "",
                        shopId: "",
                        shopName: "",
                        quantity: 0,
                        notes: "",
                      });
                    }}
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: "#6b7280",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default ProductsV2;
